import React, { useEffect, useState } from 'react'
import FontAwesome from 'react-fontawesome'

import styled from 'styled-components'

import {
  DEFAULT_SORT_ATTRIBUTE,
  SORT_ORDER, SORT_TEXT,
  DOCUMENT_ATTRIBUTE_KEYS,
  DOCUMENT_ATTRIBUTE_TITLE_LOOKUP
} from '../constants'

import { useKendra } from '../../../hooks'

const Wrapper = styled.div`
  display: flex;
  justify-content: space-evenly;
  width: 245px;

  .sorting-icons {
    color: var(--main-color)
  }

  .sort-text {
    font-size: 16px;
    color: var(--main-color);
    font-weight: 600;
    padding-top: 12px;
  }

  .sorting-attributes-dropdown {
    padding-left: 6px;
    float: left;
  }

  .sorting-order-toggle {
    padding-left: 6px;
    float: left;
  }
  

  .opt-group {
    max-height: 65px;
  }

  .toggle-button {
    border: 1.5px solid var(--main-color);
    border-radius: 5px;
    height: 24px;
    display: block;
    width: 100%;
    height: 34px;
    padding: 6px 12px;
    font-size: 14px;
    line-height: 1.42857;
    color: rgb(85,85,85);
    background-color: rgb(255,255,255);
    background-image: none;
    box-shadow: rgba(0,0,0,0.075) 0px 1px 1px inset;
    -webkit-transition: border-color 0.15s ease-in-out 0s,box-shadow 0.15s ease-in-out 0s;
    transition: border-color 0.15s ease-in-out 0s,box-shadow 0.15s ease-in-out 0s;
    cursor: pointer;

    .toggle-image {
      height: 22px;
      padding-bottom: 4px;
    }
  }
`

/**
 * Component for displaying sorting options for KENSearch results.
 * Uses the useKendra hook to access the Kendra context sans-props.
 *
 * @see {@link https://github.com/aws-samples/aws-kendra-transcribe-media-search/blob/main/finderapp/src/search/sorting/ResultSorting.tsx ResultSorting.tsx - AWS Samples}
 * @example
 * return <ResultSorting />
 *
 * @returns {JSX.Element} The rendered ResultSorting component.
 */
const ResultSorting = () => {
  const { search, setSearch,  getResultsHelper } = useKendra()
  const {
    availableSortingAttributes,
    selectedSortingAttribute,
  } = search
  const [
    sortingOrder,
    setSortingOrder
  ] = useState(selectedSortingAttribute.getSelectedSortingOrder())
  const [
    sortingAttribute,
    setSortingAttribute
  ] = useState(
    selectedSortingAttribute.getSelectedSortingAttribute()
  )

  useEffect(() => {
    setSortingAttribute(selectedSortingAttribute.getSelectedSortingAttribute())
    setSortingOrder(selectedSortingAttribute.getSelectedSortingOrder())
  }, [selectedSortingAttribute])

  /**
   * Computes the filter and resubmits the query with the updated sorting options.
   *
   * @param {string} queryText - The query text.
   * @param {number} [pageNumber=1] - The page number.
   * @param {string} [sort=sortingAttribute] - The sorting attribute.
   * @param {string} [order=sortingOrder] - The sorting order.
   * @returns {Promise<void>} The result of the query submission.
   */
  const computeFilterAndReSubmitQuery = (
    queryText,
    pageNumber = 1,
    sort = sortingAttribute,
    order = sortingOrder
  ) => {
    return getResultsHelper({ queryText, pageNumber, sort, order })
  }

  /**
   * Handles the change event for the sorting attribute dropdown.
   *
   * @param {Object} event - The change event.
   * @returns {Promise<void>} The result of the query submission.
   */
  const handleSortingAttributeChange = async event => {
    event.persist()
    setSortingAttribute(event.target.value)
    setSearch({
      type: 'SELECTED_SORTING_ATTRIBUTE',
      payload: selectedSortingAttribute
        .setSelectedSortingAttribute(event.target.value),
    });
    return computeFilterAndReSubmitQuery(
      search.queryText,
      search.currentPageNumber,
      event.target.value,
      sortingOrder
    )
  }

  /**
   * Handles the change event for the sorting order toggle button.
   *
   * @param {Object} event - The change event.
   * @returns {Promise<void>} The result of the query submission.
   */
  const handleSortingOrderChange = async event => {
    event.persist()
    const { value } = event.target
    setSortingOrder(value)
    if (value === SORT_ORDER.DESC) {
      setSearch({
        type: 'SELECTED_SORTING_ATTRIBUTE',
        payload: selectedSortingAttribute
          .setSelectedSortingOrder(SORT_ORDER.ASC),
      })
      return computeFilterAndReSubmitQuery(
        search.queryText,
        search.currentPageNumber,
        sortingAttribute,
        value
      )
    } else if (value === SORT_ORDER.ASC) {
      setSearch({
        type: 'SELECTED_SORTING_ATTRIBUTE',
        payload: selectedSortingAttribute
          .setSelectedSortingOrder(SORT_ORDER.DESC),
      })
      return computeFilterAndReSubmitQuery(
        search.queryText,
        search.currentPageNumber,
        sortingAttribute,
        value
      )
    }
  }

  /**
   * Generates the options for the sorting attribute dropdown.
   *
   * @param {Array} attributeList - The list of available sorting attributes.
   * @returns {JSX.Element} The generated options for the dropdown.
   */
  const getSortingAttributeSelectOptions = attributeList => {
    return (
      <optgroup className="opt-group">
        <option value={DEFAULT_SORT_ATTRIBUTE}>{DEFAULT_SORT_ATTRIBUTE}</option>
        {attributeList.indexOf(DOCUMENT_ATTRIBUTE_KEYS.CREATED_AT) >= 0 && (
          <option value={DOCUMENT_ATTRIBUTE_KEYS.CREATED_AT}>
            {DOCUMENT_ATTRIBUTE_TITLE_LOOKUP[DOCUMENT_ATTRIBUTE_KEYS.CREATED_AT]}
          </option>
        )}
        {attributeList.indexOf(DOCUMENT_ATTRIBUTE_KEYS.UPDATED_AT) >= 0 && (
          <option value={DOCUMENT_ATTRIBUTE_KEYS.UPDATED_AT}>
            {DOCUMENT_ATTRIBUTE_TITLE_LOOKUP[DOCUMENT_ATTRIBUTE_KEYS.UPDATED_AT]}
          </option>
        )}
        {attributeList.map((attribute, i) => (
          attribute !== DOCUMENT_ATTRIBUTE_KEYS.CREATED_AT &&
          attribute !== DOCUMENT_ATTRIBUTE_KEYS.UPDATED_AT &&
          DOCUMENT_ATTRIBUTE_TITLE_LOOKUP[attribute] && (
            <option value={attribute} key={i}>
              {DOCUMENT_ATTRIBUTE_TITLE_LOOKUP[attribute] ?? attribute}
            </option>
          )
        ))}
      </optgroup>
    );
  };

  /**
   * Renders the sorting attribute dropdown.
   *
   * @param {Array} attributeList - The list of available sorting attributes.
   * @returns {JSX.Element} The rendered sorting attribute dropdown.
   */
  const renderSortingAttributeSelect = (attributeList) => {
    return (
      <select
        name="select"
        className="sorting-attribute-select"
        onChange={handleSortingAttributeChange}
        value={selectedSortingAttribute.getSelectedSortingAttribute()}
      >
        {getSortingAttributeSelectOptions(attributeList)}
      </select>
    )
  }

  /**
   * Generates the toggle button for sorting order.
   *
   * @param {string} _sortingOrder - The current sorting order.
   * @returns {JSX.Element} The generated toggle button.
   */
  const getSortOrderToggleButton = _sortingOrder => {
    if (!_sortingOrder) {
      return (
        <button
          type="button"
          className="toggle-button"
          style={{ borderColor: 'grey', cursor: 'default' }}
          disabled={true}>
          <FontAwesome
            style={{ color: 'grey' }}
            name="fa-solid fa-arrow-down"
          />
        </button>
      )
    } else if (_sortingOrder === SORT_ORDER.DESC) {
      return (
        <button
          type="button"
          className="toggle-button"
          onClick={handleSortingOrderChange}
        >
          <FontAwesome
            className="sorting-icons"
            name="fa-solid fa-arrow-down"
          />
        </button>
      )
    } else if (_sortingOrder === SORT_ORDER.ASC) {
      return (
        <button
          type="button"
          className="toggle-button"
          onClick={handleSortingOrderChange}
        >
          <FontAwesome
            className="sorting-icons"
            name="fa-solid fa-arrow-up"
          />
        </button>
      )
    }
  }

  return (
    <Wrapper className="query-result-sorting-container">
      <span className="sort-text">{SORT_TEXT.SORT}</span>
      <div className="sorting-attributes-dropdown">
        {renderSortingAttributeSelect(availableSortingAttributes.get())}
      </div>
      <div className="sorting-order-toggle">
        {!sortingOrder && getSortOrderToggleButton(null)}
        {sortingOrder === SORT_ORDER.DESC &&
          getSortOrderToggleButton(SORT_ORDER.DESC)
        }
        {sortingOrder === SORT_ORDER.ASC &&
          getSortOrderToggleButton(SORT_ORDER.ASC)
        }
      </div>
    </Wrapper>
  )
}

export default ResultSorting